import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import routes from '@config/routes';
import { Loading } from '@goodfynd/ui';
import { useLocalStorageState } from '@hooks/useLocalStorageState';
import router from 'next/router';
import Custom500 from 'pages/500';

import { useUser } from '../../hooks';
import { useAuth } from '../../hooks/useAuth';
import type { UserInfo } from '../../stores/models/user';

import type {
  AppContextValue,
  AppDispatchContextValue,
  AppProviderProps,
  UserProfiles,
} from './types';

const STORAGE_KEY_ONBOARDING_CLOSED = 'onboarding-widget-closed';
const STORAGE_KEY_ONBOARDING_COLLAPSED = 'onboarding-widget-collapsed';

export const AppContext = createContext<AppContextValue | undefined>(undefined);
export const AppDispatchContext = createContext<
  AppDispatchContextValue | undefined
>(undefined);

export default function AppProvider({ children }: AppProviderProps) {
  const [vendorNamesById, setVendorNamesById] = useState<
    Record<string, string>
  >({});

  const defaultUserValue = useRef<UserInfo>({
    isAuthenticated: undefined,
  } as UserInfo);
  const [user, setUser] = useState<UserInfo>(defaultUserValue.current);

  // Onboarding state
  const [isOnboardingClosed, setIsOnboardingClosed] =
    useLocalStorageState<boolean>(STORAGE_KEY_ONBOARDING_CLOSED, false);
  const [isOnboardingCollapsed, setIsOnboardingCollapsed] =
    useLocalStorageState<boolean>(STORAGE_KEY_ONBOARDING_COLLAPSED, false);

  const setOnboardingClosed = useCallback(
    (closed: boolean) => {
      setIsOnboardingClosed(closed);
    },
    [setIsOnboardingClosed]
  );

  const setOnboardingCollapsed = useCallback(
    (collapsed: boolean) => {
      setIsOnboardingCollapsed(collapsed);
    },
    [setIsOnboardingCollapsed]
  );

  const notFound = useCallback(() => {
    router.push(routes.account.profile);
  }, []);

  const {
    getLoginUrl,
    isAuthenticated,
    isLoading: identityLoading,
    logout,
    user: identity,
  } = useAuth();

  const {
    error: userError,
    isFetched: userIsFetched,
    isLoading: userIsLoading,
    profileNames,
    profileRecords,
    profiles,
    refetch: refreshAuth,
  } = useUser({ enabled: isAuthenticated, identityLoading, setUser, notFound });

  const value = useMemo(
    () => ({
      identity,
      identityLoading,
      isLoggedIn: isAuthenticated,
      isOnboardingClosed: isOnboardingClosed ?? false,
      isOnboardingCollapsed: isOnboardingCollapsed ?? false,
      profileRecords,
      profiles: profiles || ([] as unknown as UserProfiles),
      user,
      userHasError: userIsFetched ? !!userError : undefined,
      userIsFetched,
      userIsLoading,
      vendorNamesById: {
        ...profileNames,
        ...vendorNamesById,
      },
    }),
    [
      identity,
      identityLoading,
      isAuthenticated,
      isOnboardingClosed,
      isOnboardingCollapsed,
      profileNames,
      profileRecords,
      profiles,
      user,
      userError,
      userIsFetched,
      userIsLoading,
      vendorNamesById,
    ]
  );

  const dispatchValue = useMemo(
    () => ({
      logout,
      getLoginUrl,
      refreshAuth,
      setOnboardingClosed,
      setOnboardingCollapsed,
      setUser,
      setVendorNamesById,
    }),
    [
      getLoginUrl,
      logout,
      refreshAuth,
      setOnboardingClosed,
      setOnboardingCollapsed,
    ]
  );

  const renderContent = useCallback(() => {
    if (identityLoading || userIsLoading) {
      return <Loading type="line" />;
    }

    if (userError && !userError.notFound) {
      return <Custom500 />;
    }

    return children;
  }, [identityLoading, userIsLoading, userError, children]);

  return (
    <AppContext.Provider value={value}>
      <AppDispatchContext.Provider value={dispatchValue}>
        {renderContent()}
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
}
